import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Quote } from 'lucide-react';

const Testimonials = () => {
  const testimonials = [
    {
      name: 'Vanessa Lopez',
      role: 'Naturopathe et Maître Reiki',
      image: 'https://gillesdelieuze.com/wp-content/uploads/2022/11/5-150x150.png',
      text: "Au départ je me suis inscrite au cycle de supervision pour les thérapeutes avec Gilles. J'avais conscience que beaucoup de croyances peuvent se créer quand on s'intéresse à la spiritualité. Au fil du parcours, j'ai lâché le mental de plus en plus. Merci Gilles pour tout l'Amour que tu transmets, pour le Cœur que tu y mets et pour ta Grande Générosité.",
    },
    {
      name: 'Clément Le Perchec',
      role: 'Enseignant spirituel',
      image: 'https://gillesdelieuze.com/wp-content/uploads/2022/11/2-150x150.png',
      text: "J'en retire une grande déconstruction, un grand changement dans mon approche de l'accompagnement des autres. J'ai gagné en structure, en profondeur et en authenticité. J'ai retrouvé beaucoup d'énergie depuis que je n'ai plus peur du monde extérieur. Gilles a été d'une générosité et d'une bienveillance incroyable.",
    },
    {
      name: 'Estelle Hôt',
      role: 'Thérapeute, professeur de Yoga',
      image: 'https://gillesdelieuze.com/wp-content/uploads/2022/11/1-150x150.png',
      text: "Ce cycle de supervision m'a confirmé que Gilles est ce pilier qui permet à chacun de se déposer en sécurité, de partager sa vulnérabilité et y découvrir sa force aussi. Je ressors de ce cycle en étant mon propre pilier, sur tous les plans, avec plus d'assurance et d'élan dans mon activité.",
    },
    {
      name: 'Elodie',
      role: 'Voyage Dauphins',
      image: 'https://gillesdelieuze.com/wp-content/uploads/2023/07/beautiful-brunette-woman-sitting-on-chair-A2KRG6A.jpg',
      text: "Ce voyage est magnifique, magique, magnétique. Les enseignements de Gilles apportent la profondeur, la sérénité pour intégrer les messages et aller à la rencontre de Soi. Tout est douceur, respect et communion. Un voyage profond qui permet le renouveau individuel et la renaissance collective.",
    },
    {
      name: 'Sylvie',
      role: 'Thérapeute',
      image: 'https://gillesdelieuze.com/wp-content/uploads/2023/04/woman-portrait-model-657753.jpg',
      text: "Le vide que j'avais en moi et que je cherchais à remplir, ce vide est définitivement comblé. Tes enseignements, accompagnements, méditations, rituels ont fait tomber encore quelques barrières. Je me sens remplie de quelque chose qui a toujours été à ma portée. Être, Vivre, sur cette Terre.",
    },
  ];

  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(0);

  const slideVariants = {
    enter: (direction) => ({
      x: direction > 0 ? 300 : -300,
      opacity: 0,
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1,
    },
    exit: (direction) => ({
      zIndex: 0,
      x: direction < 0 ? 300 : -300,
      opacity: 0,
    }),
  };

  const swipeConfidenceThreshold = 10000;
  const swipePower = (offset, velocity) => {
    return Math.abs(offset) * velocity;
  };

  const paginate = (newDirection) => {
    setDirection(newDirection);
    setCurrentIndex((prev) => {
      if (newDirection === 1) {
        return prev === testimonials.length - 1 ? 0 : prev + 1;
      }
      return prev === 0 ? testimonials.length - 1 : prev - 1;
    });
  };

  useEffect(() => {
    const timer = setInterval(() => {
      paginate(1);
    }, 6000);
    return () => clearInterval(timer);
  }, [currentIndex]);

  return (
    <section
      id="testimonials"
      data-testid="testimonials-section"
      className="relative py-24 md:py-32 bg-[#F5F5F4] overflow-hidden"
    >
      {/* Background Decoration */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#D4AF37]/5 rounded-full blur-3xl" />

      <div className="max-w-6xl mx-auto px-6 md:px-12 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <p className="text-[#D4AF37] uppercase tracking-[0.3em] text-sm font-medium mb-4">
            Témoignages
          </p>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl text-[#1C1917] leading-tight">
            Ils nous ont fait <span className="italic text-[#D4AF37]">Confiance</span>
          </h2>
        </motion.div>

        {/* Carousel */}
        <div className="relative">
          <div className="overflow-hidden">
            <motion.div
              key={currentIndex}
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{
                x: { type: 'spring', stiffness: 300, damping: 30 },
                opacity: { duration: 0.2 },
              }}
              drag="x"
              dragConstraints={{ left: 0, right: 0 }}
              dragElastic={1}
              onDragEnd={(e, { offset, velocity }) => {
                const swipe = swipePower(offset.x, velocity.x);
                if (swipe < -swipeConfidenceThreshold) {
                  paginate(1);
                } else if (swipe > swipeConfidenceThreshold) {
                  paginate(-1);
                }
              }}
              className="w-full"
              data-testid="testimonial-card"
            >
              <div className="bg-white rounded-3xl p-8 md:p-12 shadow-xl max-w-4xl mx-auto">
                <div className="flex flex-col md:flex-row gap-8 items-center md:items-start">
                  {/* Image */}
                  <div className="flex-shrink-0">
                    <div className="relative">
                      <div className="absolute -inset-2 border border-[#D4AF37]/30 rounded-full" />
                      <img
                        src={testimonials[currentIndex].image}
                        alt={testimonials[currentIndex].name}
                        className="w-24 h-24 md:w-32 md:h-32 rounded-full object-cover"
                      />
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex-grow text-center md:text-left">
                    <Quote className="text-[#D4AF37] mb-4 mx-auto md:mx-0" size={32} />
                    <p className="font-accent text-xl md:text-2xl text-[#1C1917] italic leading-relaxed mb-6">
                      {testimonials[currentIndex].text}
                    </p>
                    <div>
                      <p className="font-display text-xl text-[#1C1917]">
                        {testimonials[currentIndex].name}
                      </p>
                      <p className="text-[#D4AF37] uppercase tracking-wider text-sm">
                        {testimonials[currentIndex].role}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Navigation Arrows */}
          <button
            onClick={() => paginate(-1)}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 md:-translate-x-12 w-12 h-12 rounded-full bg-white border border-[#D4AF37]/20 flex items-center justify-center text-[#D4AF37] hover:bg-[#D4AF37] hover:text-white transition-all duration-300 shadow-lg"
            data-testid="testimonial-prev"
          >
            <ChevronLeft size={24} />
          </button>
          <button
            onClick={() => paginate(1)}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 md:translate-x-12 w-12 h-12 rounded-full bg-white border border-[#D4AF37]/20 flex items-center justify-center text-[#D4AF37] hover:bg-[#D4AF37] hover:text-white transition-all duration-300 shadow-lg"
            data-testid="testimonial-next"
          >
            <ChevronRight size={24} />
          </button>
        </div>

        {/* Dots */}
        <div className="flex justify-center gap-3 mt-8">
          {testimonials.map((_, index) => (
            <button
              key={index}
              onClick={() => {
                setDirection(index > currentIndex ? 1 : -1);
                setCurrentIndex(index);
              }}
              className={`w-3 h-3 rounded-full transition-all duration-300 ${
                index === currentIndex
                  ? 'bg-[#D4AF37] w-8'
                  : 'bg-[#D4AF37]/20 hover:bg-[#D4AF37]/50'
              }`}
              data-testid={`testimonial-dot-${index}`}
            />
          ))}
        </div>

        {/* View All Link */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="text-center mt-12"
        >
          <a
            href="https://gillesdelieuze.com/temoignages/"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="testimonials-view-all"
            className="inline-block border border-[#D4AF37] text-[#D4AF37] rounded-full px-8 py-4 uppercase tracking-widest text-sm font-medium hover:bg-[#D4AF37] hover:text-white transition-all duration-300"
          >
            Voir tous les témoignages
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default Testimonials;
